import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, Upload, Send, Columns3, Mail, Search, Check, X, FileUp, Trash2,
  Users, BadgeCheck, ArrowLeft, FileSpreadsheet, Download, Paperclip, Link2, Plus, Lock, UploadCloud, Pencil,
  Trophy, ReceiptText, Clock, CheckCircle2,
} from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VendorQuoteDialog } from "@/components/VendorQuoteDialog";
import { useAuth } from "@/context/AuthContext";
import { RfqTypeBadge, RfqTypePicker } from "@/components/RfqType";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";

const DELIVERY_LABEL = { queued: "Sending", sent: "Sent", delivered: "Delivered", delayed: "Delayed", opened: "Opened",
  clicked: "Clicked", bounced: "Bounced", complained: "Marked spam", failed: "Not sent" };
const DELIVERY_TINT = {
  delivered: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
  opened: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
  clicked: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
  delayed: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
  bounced: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
  complained: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
};
const DELIVERY_HINT = {
  bounced: "The address rejected it — check the spelling or ask the vendor for another address",
  complained: "The recipient marked it as spam, so Matiqs will not email them again",
  delayed: "Their mail server is retrying — usually clears by itself",
  failed: "Matiqs could not hand it to the mail provider",
};

const Section = ({ title, hint, right, children, testid }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-2xl">
    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-border">
      <div>
        <div className="font-semibold text-slate-900 dark:text-slate-100">{title}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
      </div>
      {right}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const download = (url, filename) => {
  const token = localStorage.getItem("matiqs_token");
  fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (!r.ok) throw new Error("download failed"); return r.blob(); })
    .then((b) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => toast.error("Could not download that file"));
};

export default function BuyerRfqDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const boqRef = useRef();
  const docRef = useRef();
  const quoteRef = useRef();
  const [rfq, setRfq] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  const [vendorQuery, setVendorQuery] = useState("");
  const [vendors, setVendors] = useState([]);
  const [picked, setPicked] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [approved, setApproved] = useState([]);
  const [pickedApproved, setPickedApproved] = useState([]);

  const [manual, setManual] = useState({ vendor_name: "", email: "", contact_person: "", contact_phone: "", total: "", discount: "", delivery_days: "", validity_days: 30, notes: "" });
  const [savingManual, setSavingManual] = useState(false);
  const [quoteVendor, setQuoteVendor] = useState("");
  const [dragging, setDragging] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorDraft, setVendorDraft] = useState("");
  const { user } = useAuth();
  const [inviteAllCountries, setInviteAllCountries] = useState(false);
  const [vendorRegion, setVendorRegion] = useState("");
  const [regions, setRegions] = useState([]);
  const [openQuote, setOpenQuote] = useState("");
  const [extContact, setExtContact] = useState({ contact_person: "", contact_email: "", contact_phone: "" });
  const [showManual, setShowManual] = useState(false);
  const [billing, setBilling] = useState(null);
  const [poStatus, setPoStatus] = useState(null);
  const [poRef, setPoRef] = useState("");
  const [poBusy, setPoBusy] = useState(false);
  const poFileRef = useRef(null);

  const loadPoStatus = () =>
    api.get(`/buyer/rfqs/${id}/po-status`).then((r) => setPoStatus(r.data)).catch(() => {});

  const uploadPo = async () => {
    setPoBusy(true);
    try {
      const fd = new FormData();
      fd.append("reference", poRef || "");
      const f = poFileRef.current?.files?.[0];
      if (f) fd.append("file", f);
      await api.post(`/buyer/rfqs/${id}/po`, fd);
      toast.success("Purchase Order sent to the vendor");
      if (poFileRef.current) poFileRef.current.value = "";
      setPoRef("");
      loadPoStatus();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not upload the Purchase Order");
    } finally { setPoBusy(false); }
  };

  const apply = (data) => {
    setRfq(data);
    setForm({
      rfq_ref: data.rfq_ref || "", title: data.title || "", project: data.project || "",
      location: data.location || "", due_date: data.due_date || "", notes: data.notes || "",
      package_url: data.package_url || "", contact_person: data.contact_person || "",
      contact_designation: data.contact_designation || "", contact_phone: data.contact_phone || "",
      contact_email: data.contact_email || "", rfq_type: data.rfq_type || "tender",
    });
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/buyer/rfqs/${id}`);
      apply(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "RFQ not found");
      navigate("/rfqs");
    }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (rfq?.awarded_quote_id) loadPoStatus(); }, [rfq?.awarded_quote_id]);

  useEffect(() => {
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
    api.get("/public/countries").then((r) => setRegions(r.data.regions || [])).catch(() => {});
    api.get("/buyer/approved-vendors").then((r) => setApproved(r.data.vendors || [])).catch(() => {});
  }, []);

  const MAX_APPROVED = 10;
  const tradeInit = useRef("");
  useEffect(() => {
    if (rfq?.id && rfq.id !== tradeInit.current && rfq.trades?.length && !vendorQuery) {
      tradeInit.current = rfq.id;
      setVendorQuery(rfq.trades[0]);
    }
  }, [rfq]);
  const rfqTrades = (rfq?.trades || []).map((t) => t.toLowerCase());
  const matchesTrade = (t) => {
    const tl = (t || "").toLowerCase();
    return rfqTrades.some((rt) => rt && (tl.includes(rt) || rt.includes(tl)));
  };
  const sortedApproved = [...approved].sort((a, b) => {
    const am = matchesTrade(a.trade) ? 0 : 1;
    const bm = matchesTrade(b.trade) ? 0 : 1;
    return am - bm || a.company_name.localeCompare(b.company_name);
  });
  const toggleApproved = (v) => {
    setPickedApproved((cur) => {
      if (cur.includes(v.id)) return cur.filter((x) => x !== v.id);
      if (cur.length >= MAX_APPROVED) {
        toast.warning(`You can pick up to ${MAX_APPROVED} approved vendors at a time`);
        return cur;
      }
      return [...cur, v.id];
    });
  };

  useEffect(() => {
    let params;
    if (vendorRegion) {
      params = { q: vendorQuery, region: vendorRegion };
    } else {
      params = { q: vendorQuery, country: inviteAllCountries ? "" : (user?.country || "") };
    }
    api.get("/public/vendors", { params })
      .then((r) => setVendors((r.data.vendors || []).slice(0, 8)))
      .catch(() => {});
  }, [vendorQuery, inviteAllCountries, vendorRegion, user]);

  const saveDetails = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/buyer/rfqs/${id}`, form);
      apply(data);
      toast.success("RFQ details saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const uploadBoq = async (file) => {
    if (!file) return;
    setUploading("boq");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/buyer/rfqs/${id}/boq`, fd);
      apply(data);
      toast.success(data.boq.item_count > 0
        ? `BOQ attached — ${data.boq.item_count} line items read for comparison`
        : "BOQ attached — vendors will receive the PDF as-is", {
        description: [
          data.boq.units_normalised ? `${data.boq.units_normalised} unit(s) standardised` : "",
          data.trades?.length ? `Looks like ${data.trades.join(", ")}` : "",
        ].filter(Boolean).join(" · ") || undefined,
        duration: 7000,
      });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not read that file", { duration: 7000 });
    } finally {
      setUploading("");
      if (boqRef.current) boqRef.current.value = "";
    }
  };

  const removeBoq = async () => {
    if (!window.confirm("Remove the attached BOQ? Any line-level comparison for this RFQ will be cleared.")) return;
    try {
      const { data } = await api.delete(`/buyer/rfqs/${id}/boq`);
      apply(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading("doc");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/buyer/rfqs/${id}/attachments`, fd);
      toast.success(`${file.name} attached`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not upload that file", { duration: 7000 });
    } finally {
      setUploading("");
      if (docRef.current) docRef.current.value = "";
    }
  };

  const removeDoc = async (aid) => {
    try {
      await api.delete(`/buyer/rfqs/${id}/attachments/${aid}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addEmail = () => {
    const found = (emailInput.match(/[^@\s<>,;()[\]"]+@[^@\s<>,;()[\]"]+\.[A-Za-z]{2,}/g) || [])
      .map((x) => x.replace(/[.,;:]+$/, "").toLowerCase());
    const already = new Set([...emails, ...(rfq?.invited || []).map((i) => i.email)]);
    const fresh = [];
    found.forEach((em) => {
      if (!already.has(em)) {
        already.add(em);
        fresh.push(em);
      }
    });
    const leftovers = emailInput
      .split(/[\s,;]+/)
      .map((x) => x.trim().replace(/[<>]/g, "").toLowerCase())
      .filter((x) => x.includes("@") && !found.includes(x.replace(/[.,;:]+$/, "")));
    if (!found.length) return toast.error("No valid email address found in that text");
    setEmails([...emails, ...fresh]);
    setEmailInput("");
    const dupes = found.length - fresh.length;
    const parts = [`${fresh.length} address${fresh.length === 1 ? "" : "es"} added`];
    if (dupes) parts.push(`${dupes} already on the list`);
    if (leftovers.length) parts.push(`${leftovers.length} invalid address${leftovers.length === 1 ? "" : "es"} ignored`);
    toast.success(parts.join(" · "));
  };

  const invite = async () => {
    const approvedEmails = approved.filter((v) => pickedApproved.includes(v.id)).map((v) => v.email);
    const allEmails = [...new Set([...emails, ...approvedEmails])];
    if (!picked.length && !allEmails.length) return toast.error("Pick at least one vendor or add an email");
    setInviting(true);
    try {
      const { data } = await api.post(`/buyer/rfqs/${id}/invite`, { vendor_ids: picked, emails: allEmails });
      apply(data.rfq);
      setPicked([]);
      setEmails([]);
      setPickedApproved([]);
      if (data.invited === 0) {
        toast.info("Those vendors were already invited to this RFQ — no new invites sent");
      } else {
        toast.success(`RFQ sent to ${data.invited} vendor${data.invited === 1 ? "" : "s"}`);
      }
      if (data.opted_out?.length) {
        toast.warning(`Skipped ${data.opted_out.length} address that opted out of RFQ invitations`,
          { description: data.opted_out.join(", "), duration: 9000 });
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send the RFQ", { duration: 7000 });
    } finally {
      setInviting(false);
    }
  };

  const addManualQuote = async (e) => {
    e.preventDefault();
    setSavingManual(true);
    try {
      await api.post(`/buyer/rfqs/${id}/quotes/manual`, {
        vendor_name: manual.vendor_name,
        email: manual.email,
        contact_person: manual.contact_person,
        contact_phone: manual.contact_phone,
        total: Number(manual.total) || 0,
        discount: Number(manual.discount) || 0,
        delivery_days: Number(manual.delivery_days) || 0,
        validity_days: Number(manual.validity_days) || 30,
        notes: manual.notes,
      });
      setManual({ vendor_name: "", email: "", contact_person: "", contact_phone: "", total: "", discount: "", delivery_days: "", validity_days: 30, notes: "" });
      toast.success("Quote added to the comparison");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not add the quote");
    } finally {
      setSavingManual(false);
    }
  };

  const uploadQuote = async (file) => {
    if (!file) return;
    setUploading("quote");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ vendor_name: quoteVendor.trim(), ...extContact });
      const { data } = await api.post(`/buyer/rfqs/${id}/quotes/upload?${params}`, fd);
      toast.success(
        `Read ${data.lines_read} of ${data.items} rates from ${data.vendor_name || "the quote"}` +
        (data.extras ? ` · ${data.extras} line${data.extras === 1 ? "" : "s"} they added` : ""));
      if (data.duplicate_of) {
        toast.warning(`This looks like a quote you already added from "${data.duplicate_of}". Added anyway — delete it if it's a duplicate.`, { duration: 9000 });
      }
      setQuoteVendor("");
      setExtContact({ contact_person: "", contact_email: "", contact_phone: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not read that quote", { duration: 8000 });
    } finally {
      setUploading("");
      if (quoteRef.current) quoteRef.current.value = "";
    }
  };

  const dropQuotes = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.(pdf|xlsx|xls|csv)$/i.test(f.name));
    if (!files.length) { toast.error("Drop PDF or Excel quotation files"); return; }
    if (!canExternal) { navigate("/billing"); return; }
    setUploading("quote");
    let ok = 0; const failed = []; const dups = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      const params = new URLSearchParams({ vendor_name: quoteVendor.trim(), ...extContact });
      try {
        const { data } = await api.post(`/buyer/rfqs/${id}/quotes/upload?${params}`, fd);
        ok += 1;
        if (data.duplicate_of) dups.push(data.duplicate_of);
      } catch (e) { failed.push(f.name); }
    }
    if (ok) toast.success(`${ok} quote${ok === 1 ? "" : "s"} added to the comparison`);
    if (dups.length) toast.warning(`Possible duplicate${dups.length === 1 ? "" : "s"} for: ${[...new Set(dups)].join(", ")} — delete any repeats.`, { duration: 9000 });
    if (failed.length) toast.warning(`${failed.length} file(s) couldn't be read — add those totals by hand`);
    setQuoteVendor("");
    setExtContact({ contact_person: "", contact_email: "", contact_phone: "" });
    setUploading("");
    load();
  };

  const removeQuote = async (qid) => {
    try {
      await api.delete(`/buyer/rfqs/${id}/quotes/${qid}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const renameVendor = async (qid) => {
    const name = vendorDraft.trim();
    if (!name) { toast.error("Vendor name cannot be empty"); return; }
    try {
      await api.put(`/buyer/rfqs/${id}/quotes/${qid}/vendor`, { vendor_name: name });
      setEditingVendor(null);
      toast.success("Vendor name updated");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not rename vendor");
    }
  };

  if (!rfq || !form) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const isQompare = rfq?.kind === "qompare";
  const canExternal = isQompare || Boolean(billing?.features?.external_quotes);

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div data-testid="buyer-rfq-detail" className="space-y-6">
      <button onClick={() => navigate("/rfqs")} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
        <ArrowLeft className="w-4 h-4" /> All RFQs
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold font-mono">
            {rfq.rfq_ref} · {rfq.status === "draft" ? "Draft" : rfq.status}
          </div>
          <h1 data-testid="rfq-heading" className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">
            {rfq.title}
          </h1>
          <div className="mt-2"><RfqTypeBadge type={form.rfq_type} /></div>
          <p className="text-sm text-muted-foreground mt-1">
            {[rfq.project, rfq.location, rfq.due_date && `Quotes due ${rfq.due_date}`].filter(Boolean).join(" · ") || "No project details set"}
          </p>
        </div>
        {rfq.quote_count > 0 && (
          <Button data-testid="open-comparison-button" onClick={() => navigate(`/compare/${id}`)} className="h-11 gap-2 font-semibold">
            <Columns3 className="w-4 h-4" /> Compare {rfq.quote_count} quotes
          </Button>
        )}
      </div>

      <Section
        testid="details-section"
        title="RFQ & project details"
        hint="This is what vendors see in the invitation."
        right={
          <Button data-testid="save-details-button" onClick={saveDetails} disabled={saving} className="h-10 gap-2 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save details
          </Button>
        }
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="d-ref">RFQ reference</Label>
            <Input id="d-ref" data-testid="detail-ref-input" {...f("rfq_ref")} className="mt-1.5 font-mono" />
          </div>
          <div>
            <Label>Enquiry type</Label>
            <div className="mt-1.5">
              <RfqTypePicker value={form.rfq_type || "tender"}
                             onChange={(v) => setForm({ ...form, rfq_type: v })}
                             testidPrefix="detail-rfq-type" />
            </div>
          </div>
          <div className="lg:col-span-2">
            <Label htmlFor="d-title">RFQ title</Label>
            <Input id="d-title" data-testid="detail-title-input" {...f("title")} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="d-project">Project</Label>
            <Input id="d-project" data-testid="detail-project-input" {...f("project")} placeholder="e.g. Marina Heights" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="d-location">Location</Label>
            <Input id="d-location" data-testid="detail-location-input" {...f("location")} placeholder="e.g. Dubai, UAE" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="d-due">Quotes due by</Label>
            <Input id="d-due" type="date" data-testid="detail-due-input" {...f("due_date")} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <Label htmlFor="d-notes">Scope notes / instructions to vendors</Label>
            <Textarea id="d-notes" data-testid="detail-notes-input" {...f("notes")} rows={3}
                      placeholder="Exclusions, delivery expectations, site conditions, submission requirements…" className="mt-1.5" />
          </div>

          <div className="sm:col-span-2 lg:col-span-3 border-t border-border pt-4 mt-1">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Contact details</div>
          </div>
          <div>
            <Label htmlFor="d-person">Contact person</Label>
            <Input id="d-person" data-testid="detail-contact-person-input" {...f("contact_person")} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="d-desig">Designation</Label>
            <Input id="d-desig" data-testid="detail-contact-designation-input" {...f("contact_designation")} placeholder="e.g. Procurement Manager" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="d-phone">Phone</Label>
            <Input id="d-phone" data-testid="detail-contact-phone-input" {...f("contact_phone")} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="d-cemail">Email for quotations</Label>
            <Input id="d-cemail" type="email" data-testid="detail-contact-email-input" {...f("contact_email")} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <Label htmlFor="d-package" className="flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" /> Full package download link
            </Label>
            <Input id="d-package" data-testid="detail-package-url-input" {...f("package_url")}
                   placeholder="https://… (Drive, Dropbox, WeTransfer — for drawings too large to attach)" className="mt-1.5" />
          </div>
        </div>
      </Section>

      <Section
        testid="boq-section"
        title="Bill of quantities"
        right={
          <div className="flex items-center gap-2">
            <input ref={boqRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden"
                   data-testid="boq-file-input" onChange={(e) => uploadBoq(e.target.files[0])} />
            <Button variant={rfq.boq ? "outline" : "default"} data-testid="upload-boq-button"
                    onClick={() => boqRef.current?.click()} disabled={uploading === "boq"} className="h-10 gap-2 font-semibold">
              {uploading === "boq" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {rfq.boq ? "Replace BOQ" : "Attach BOQ (Excel or PDF)"}
            </Button>
          </div>
        }
        hint="Attach the BOQ. Excel or CSV gives you a line-by-line comparison; a PDF is passed to vendors as-is."
      >
        {rfq.boq ? (
          <div data-testid="boq-summary" className="flex flex-wrap items-center gap-4 px-4 py-3.5 rounded-xl border border-border bg-secondary/40">
            <FileSpreadsheet className="w-8 h-8 text-brand shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">{rfq.boq.filename}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {rfq.boq.item_count > 0
                  ? `${rfq.boq.item_count} priceable line items${rfq.boq.sheets?.length > 1 ? ` · ${rfq.boq.sheets.length} sheets` : ""}`
                  : "PDF — vendors quote from the file itself, comparison shows headline totals"} · {kb(rfq.boq.size)}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" data-testid="download-boq-button"
                      onClick={() => download(`/buyer/rfqs/${id}/boq/file`, rfq.boq.filename)} className="h-9 gap-2">
                <Download className="w-4 h-4" /> Download
              </Button>
              <button data-testid="remove-boq-button" onClick={removeBoq}
                      className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <FileSpreadsheet className="w-8 h-8 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground mt-3">
            No BOQ attached yet. Excel, CSV or PDF — vendors receive the file exactly as you upload it.
            </p>
          </div>
        )}
      </Section>

      <Section
        testid="documents-section"
        title="Documents & drawings"
        hint="PDF, Excel, Word, drawings (DWG/DXF/RVT), images or a ZIP — up to 40 MB each."
        right={
          <div className="flex items-center gap-2">
            <input ref={docRef} type="file" className="hidden"
                   accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.zip,.rar,.7z,.dwg,.dxf,.rvt,.png,.jpg,.jpeg"
                   data-testid="doc-file-input" onChange={(e) => uploadDoc(e.target.files[0])} />
            <Button variant="outline" data-testid="upload-doc-button" onClick={() => docRef.current?.click()}
                    disabled={uploading === "doc"} className="h-10 gap-2 font-semibold">
              {uploading === "doc" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Add document
            </Button>
          </div>
        }
      >
        {rfq.attachments?.length > 0 ? (
          <div data-testid="attachment-list" className="space-y-2">
            {rfq.attachments.map((a, i) => (
              <div key={a.id} data-testid={`attachment-${i}`} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border">
                <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{a.filename}</div>
                  <div className="text-xs text-muted-foreground">{kb(a.size)}</div>
                </div>
                <button data-testid={`download-attachment-${i}`}
                        onClick={() => download(`/buyer/rfqs/${id}/attachments/${a.id}/file`, a.filename)}
                        className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-primary transition-colors">
                  <Download className="w-4 h-4" />
                </button>
                <button data-testid={`remove-attachment-${i}`} onClick={() => removeDoc(a.id)}
                        className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            Nothing attached yet. Add specifications, drawings or a tender pack ZIP.
          </p>
        )}
      </Section>

      <Section
        testid="invite-section"
        title="Invite vendors"
        hint="Matiqs vendors get it in their Opportunities inbox with every file. Anyone else gets it by email."
        right={
          <Button data-testid="send-rfq-button" onClick={invite} disabled={inviting} className="h-10 gap-2 font-semibold">
            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send RFQ{picked.length + emails.length + pickedApproved.length > 0 ? ` (${picked.length + emails.length + pickedApproved.length})` : ""}
          </Button>
        }
      >
        {approved.length > 0 && (
          <div data-testid="approved-vendor-picker" className="mb-6 rounded-xl border border-border bg-secondary/30 p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <Label className="flex items-center gap-1.5 mb-0">
                <Users className="w-4 h-4 text-brand" /> From your approved vendors
              </Label>
              <span className="text-[11px] text-muted-foreground">{pickedApproved.length}/{MAX_APPROVED} selected{rfq.trades?.length ? " · trade matches first" : ""}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
              {sortedApproved.map((v, i) => {
                const on = pickedApproved.includes(v.id);
                const hit = matchesTrade(v.trade);
                return (
                  <button key={v.id} data-testid={`pick-approved-${i}`} onClick={() => toggleApproved(v)}
                          className={`text-left px-3 py-2 rounded-lg border flex items-center gap-2.5 transition-all ${on ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 bg-card"}`}>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-primary border-primary" : "border-border"}`}>
                      {on && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                        {v.company_name}
                        {v.on_matiqs && <BadgeCheck className="w-3.5 h-3.5 text-brand shrink-0" />}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">{v.email}{v.trade ? ` · ${v.trade}` : ""}</div>
                    </div>
                    {hit && <span className="text-[10px] font-bold uppercase text-brand bg-brand-soft px-1.5 py-0.5 rounded shrink-0">trade</span>}
                  </button>
                );
              })}
            </div>
            <a href="/my-vendors" data-testid="manage-vendors-link" className="inline-block mt-2 text-xs font-semibold text-brand hover:underline">Manage my vendors →</a>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="mb-2 block">Suggested Matiqs vendors for this trade</Label>
            {rfq.trades?.length > 0 && (
              <div data-testid="rfq-trades" className="flex flex-wrap items-center gap-1.5 mb-2">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Looks like</span>
                {rfq.trades.map((t) => (
                  <button key={t} data-testid={`rfq-trade-${t}`} onClick={() => setVendorQuery(t)}
                          className="px-2 py-0.5 rounded-full bg-brand-soft text-brand text-[11px] font-bold hover:opacity-80 transition-opacity">
                    {t}
                  </button>
                ))}
                <span className="text-[11px] text-muted-foreground">— tap to find those vendors</span>
              </div>
            )}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="vendor-search-input" value={vendorQuery}
                     onChange={(e) => setVendorQuery(e.target.value)}
                     placeholder="Search by trade or company" className="pl-9 h-10" />
            </div>
            {regions.length > 0 && (
              <div data-testid="rfq-region-chips" className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground">Region:</span>
                {regions.map((rg) => (
                  <button
                    key={rg}
                    data-testid={`rfq-region-${rg}`}
                    onClick={() => setVendorRegion(vendorRegion === rg ? "" : rg)}
                    className={`px-2 py-0.5 rounded-full text-[11px] font-bold border transition-colors ${vendorRegion === rg ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}
                  >
                    {rg}
                  </button>
                ))}
              </div>
            )}
            {user?.country && !vendorRegion && (
              <label data-testid="invite-all-countries-toggle" className="mt-2 flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={inviteAllCountries}
                       onChange={(e) => setInviteAllCountries(e.target.checked)}
                       className="rounded border-border" />
                {inviteAllCountries ? "Showing vendors in all countries" : `Showing vendors in ${user.country} only`}
              </label>
            )}
            <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
              {vendors.map((v, i) => {
                const on = picked.includes(v.id);
                return (
                  <button
                    key={v.id}
                    data-testid={`pick-vendor-${i}`}
                    onClick={() => setPicked(on ? picked.filter((x) => x !== v.id) : [...picked, v.id])}
                    className={`w-full text-left px-3 py-2.5 rounded-xl border flex items-center gap-3 transition-all ${on ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                  >
                    <div className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${on ? "bg-primary border-primary" : "border-border"}`}>
                      {on && <Check className="w-3.5 h-3.5 text-primary-foreground" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{v.company_name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {v.business_types?.slice(0, 3).join(" · ") || v.vendor_type}
                      </div>
                    </div>
                    {v.auto_quote && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="shrink-0 cursor-help" data-testid="invite-auto-quote-badge">
                              <BadgeCheck className="w-4 h-4 text-brand" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-[240px] text-center">
                            Auto quote: this vendor prices your BOQ automatically from their catalogue — expect a fast, itemised quote.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="invite-email" className="mb-2 block">By email address</Label>
            <Textarea id="invite-email" data-testid="invite-email-input" value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onPaste={(e) => {
                        const text = (e.clipboardData || window.clipboardData).getData("text");
                        if (text && /[,;\n]/.test(text)) {
                          e.preventDefault();
                          setEmailInput(emailInput ? `${emailInput}, ${text}` : text);
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addEmail(); } }}
                      rows={3}
                      placeholder={"Paste a whole list — commas, semicolons, spaces or new lines all work\nestimation@vendor.com, Ahmed Ali <ahmed@abc.ae>; sales@xyz.com"}
                      className="text-sm" />
            <div className="flex items-center justify-between gap-2 mt-2">
              <p className="text-xs text-muted-foreground">
                Each vendor gets their own separate email — never CC'd, so nobody sees who else you asked.
              </p>
              <Button variant="outline" data-testid="add-email-button" onClick={addEmail} className="h-9 shrink-0">
                Add {emailInput.trim() ? "list" : ""}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {emails.length > 1 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
                  {emails.length} to send
                  <button data-testid="clear-emails" onClick={() => setEmails([])} className="hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {emails.map((em) => (
                <span key={em} data-testid={`email-chip-${em}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary text-xs font-semibold">
                  <Mail className="w-3 h-3" /> {em}
                  <button onClick={() => setEmails(emails.filter((x) => x !== em))} className="text-muted-foreground hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            {rfq.invited?.length > 0 && (
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-3 flex items-center gap-1.5">
                  <Users className="w-3 h-3" /> Already invited
                </div>
                <div data-testid="invited-list" className="space-y-1.5">
                  {rfq.invited.map((inv, i) => (
                    <div key={inv.id} data-testid={`invited-${i}`} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate font-medium" title={inv.email}>{inv.company_name || inv.email}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        {inv.delivery && (
                          <span data-testid={`delivery-${i}`} className={`px-2 py-0.5 rounded-md font-semibold ${DELIVERY_TINT[inv.delivery] || "bg-secondary text-muted-foreground"}`}
                                title={DELIVERY_HINT[inv.delivery] || ""}>
                            {DELIVERY_LABEL[inv.delivery] || inv.delivery}
                          </span>
                        )}
                        <span className="text-muted-foreground">{inv.channel === "matiqs" ? "In app" : "Email"}</span>
                        <span className={`px-2 py-0.5 rounded-md font-semibold ${inv.status === "quoted" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" : "bg-secondary text-muted-foreground"}`}>
                          {inv.status}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>

      {false && rfq.awarded_quote_id && poStatus?.awarded && (
      <Section
        testid="po-status-section"
        title="Awarded vendor — Purchase Order & invoice"
        hint="Send your Purchase Order / contract to the awarded vendor. They can only raise a tax invoice once it's received."
      >
        <div data-testid="po-status-card" className="rounded-2xl border border-emerald-300 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/30 p-5">
          <div className="flex items-center gap-2 font-bold text-emerald-800 dark:text-emerald-200">
            <Trophy className="w-4 h-4" /> {poStatus.vendor_name || "Awarded vendor"}
          </div>

          {!poStatus.on_matiqs ? (
            <p className="text-sm text-muted-foreground mt-2">
              This vendor isn't on Matiqs, so e-invoicing runs off-platform. Send them your Purchase Order directly.
            </p>
          ) : (
            <>
              <div className="grid sm:grid-cols-3 gap-3 mt-4">
                <div className="rounded-xl border border-border bg-card px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">PO requested by vendor</div>
                  <div className="text-sm font-semibold mt-1 flex items-center gap-1.5">
                    {poStatus.po.requested
                      ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Yes</>
                      : <><Clock className="w-4 h-4 text-muted-foreground" /> Not yet</>}
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-card px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Purchase Order</div>
                  <div className="text-sm font-semibold mt-1 flex items-center gap-1.5">
                    {poStatus.po.received
                      ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> On record{poStatus.po.reference ? ` · ${poStatus.po.reference}` : ""}</>
                      : <><Clock className="w-4 h-4 text-muted-foreground" /> Not sent</>}
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-card px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Tax invoice</div>
                  <div className="text-sm font-semibold mt-1 flex items-center gap-1.5">
                    {poStatus.invoice
                      ? <><ReceiptText className="w-4 h-4 text-emerald-600" /> {poStatus.invoice.number} · {poStatus.invoice.currency} {Number(poStatus.invoice.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                      : <><Clock className="w-4 h-4 text-muted-foreground" /> Pending</>}
                  </div>
                </div>
              </div>
              {poStatus.credit_note && (
                <div data-testid="po-credit-note" className="text-sm text-rose-600 dark:text-rose-400 mt-3">
                  Credit note issued: {poStatus.credit_note.number} · {poStatus.credit_note.currency} {Number(poStatus.credit_note.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              )}

              <div className="mt-4 border-t border-emerald-200 dark:border-emerald-900 pt-4">
                <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  <Upload className="w-4 h-4" /> {poStatus.po.received ? "Replace Purchase Order / contract" : "Upload Purchase Order / contract"}
                </div>
                <div className="flex flex-wrap items-end gap-3 mt-3">
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Reference (optional)</label>
                    <Input data-testid="buyer-po-reference" value={poRef} onChange={(e) => setPoRef(e.target.value)}
                           placeholder="e.g. PO-2026-00123" className="mt-1 h-10" />
                  </div>
                  <input ref={poFileRef} type="file" data-testid="buyer-po-file" className="text-sm max-w-[220px]"
                         accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" />
                  <Button data-testid="buyer-upload-po-button" onClick={uploadPo} disabled={poBusy} className="h-10 gap-2 font-semibold">
                    {poBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to vendor
                  </Button>
                  {poStatus.po.has_file && (
                    <button data-testid="buyer-po-download" onClick={() => {
                        const token = localStorage.getItem("matiqs_token");
                        fetch(`${API}/buyer/rfqs/${id}/po/file`, { headers: { Authorization: `Bearer ${token}` } })
                          .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
                          .then((b) => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "purchase-order"; a.click(); URL.revokeObjectURL(a.href); })
                          .catch(() => toast.error("Could not download the file"));
                      }} className="text-sm font-semibold text-brand hover:underline">View current PO</button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Section>
      )}

      {rfq.quotes?.length > 0 && (
      <Section
        testid="quotes-section"
        title="Quotes received"
        hint="Vendors quoting through Matiqs appear automatically."
      >
          <div data-testid="quote-list" className="space-y-2 mb-7">
            {rfq.quotes.map((q, i) => (
              <div key={q.id} data-testid={`quote-${i}`} className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-border hover:border-primary/40 transition-colors">
                <div className="min-w-0 flex-1">
                  {editingVendor === q.id ? (
                    <div className="flex items-center gap-2">
                      <Input data-testid={`vendor-name-edit-${i}`} autoFocus value={vendorDraft}
                             onChange={(e) => setVendorDraft(e.target.value)}
                             onKeyDown={(e) => { if (e.key === "Enter") renameVendor(q.id); if (e.key === "Escape") setEditingVendor(null); }}
                             className="h-8 text-sm max-w-xs" />
                      <button data-testid={`vendor-name-save-${i}`} onClick={() => renameVendor(q.id)} className="text-primary hover:opacity-80 shrink-0"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingVendor(null)} className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`open-quote-${i}`} onClick={() => setOpenQuote(q.id)} className="text-sm font-semibold truncate hover:text-primary transition-colors text-left">{q.vendor_name}</button>
                      <button data-testid={`vendor-name-edit-btn-${i}`} title="Rename vendor"
                              onClick={() => { setEditingVendor(q.id); setVendorDraft(q.vendor_name || ""); }}
                              className="text-muted-foreground hover:text-primary shrink-0"><Pencil className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  <button onClick={() => setOpenQuote(q.id)} className="block text-left text-xs text-muted-foreground hover:text-foreground transition-colors">
                    {q.source === "matiqs" ? "Submitted via Matiqs"
                      : q.source === "excel_ai" ? "Excel read by AI"
                      : q.source === "pdf_ai" ? "PDF read by AI" : "Entered manually"}
                    {q.delivery_days ? ` · ${q.delivery_days} days delivery` : ""}
                    {q.bafo?.status === "requested" ? " · best & final requested" : q.bafo?.status === "responded" ? " · best & final received" : ""}
                  </button>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono font-bold tabular-nums">{rfq.currency} {Number(q.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  <button data-testid={`remove-quote-${i}`} onClick={() => removeQuote(q.id)}
                          className="w-8 h-8 rounded-md text-muted-foreground hover:text-destructive flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
      </Section>
      )}

      <Section
        testid="add-quote-section"
        title="Add a quotation"
        hint="Add emailed replies from external vendors, or enter headline numbers by hand."
      >
        <div className="space-y-5">
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">Add a quotation from an external vendor</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl leading-relaxed">
                  Upload the Excel or PDF quotation they emailed you. AI maps their prices onto your BOQ
                  reference so they land in the comparison line by line — including anything they added.
                </p>
              </div>
              {!canExternal && (
                <button data-testid="external-upgrade-link" onClick={() => navigate("/billing")}
                        className="text-xs font-bold text-brand hover:underline shrink-0">
                  Standard & Pro feature — see plans
                </button>
              )}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              <Input data-testid="quote-vendor-name-input" value={quoteVendor}
                     onChange={(e) => setQuoteVendor(e.target.value)}
                     placeholder="Vendor name (optional — we read it from the file)" className="h-10" />
              <Input data-testid="quote-contact-person-input" value={extContact.contact_person}
                     onChange={(e) => setExtContact({ ...extContact, contact_person: e.target.value })}
                     placeholder="Contact person" className="h-10" />
              <Input data-testid="quote-contact-email-input" value={extContact.contact_email}
                     onChange={(e) => setExtContact({ ...extContact, contact_email: e.target.value })}
                     placeholder="Email" className="h-10" />
              <Input data-testid="quote-contact-phone-input" value={extContact.contact_phone}
                     onChange={(e) => setExtContact({ ...extContact, contact_phone: e.target.value })}
                     placeholder="Phone" className="h-10" />
            </div>

            <input ref={quoteRef} type="file" accept=".xlsx,.xls,.csv,.pdf" multiple className="hidden"
                   data-testid="quote-file-input"
                   onChange={(e) => { const fs = e.target.files; if (fs?.length > 1) dropQuotes(fs); else uploadQuote(fs[0]); if (quoteRef.current) quoteRef.current.value = ""; }} />

            <div
              data-testid="quote-dropzone"
              onDragOver={(e) => { if (canExternal) { e.preventDefault(); setDragging(true); } }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); if (canExternal && uploading !== "quote") dropQuotes(e.dataTransfer.files); }}
              onClick={() => (canExternal ? (uploading !== "quote" && quoteRef.current?.click()) : navigate("/billing"))}
              className={`mt-4 rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragging ? "border-primary bg-brand-soft/50" : "border-primary/25 hover:border-primary/50 bg-card"}`}
            >
              {uploading === "quote" ? (
                <div className="flex items-center justify-center gap-2 text-primary text-sm font-semibold">
                  <Loader2 className="w-4 h-4 animate-spin" /> Reading the quote(s)…
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1.5">
                  <div className="w-11 h-11 rounded-xl bg-brand-soft text-brand flex items-center justify-center">
                    {canExternal ? <UploadCloud className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-1">
                    Drop more quotes here — straight from your email
                  </div>
                  <p className="text-xs text-muted-foreground">
                    PDF or Excel, one or many. We read the supplier's name and prices for you.
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <Button data-testid="upload-quote-button"
                      onClick={() => (canExternal ? quoteRef.current?.click() : navigate("/billing"))}
                      disabled={uploading === "quote"} variant="outline" className="h-10 gap-2 font-semibold">
                {uploading === "quote" ? <Loader2 className="w-4 h-4 animate-spin" />
                  : canExternal ? <FileUp className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                Choose quote files
              </Button>
              <button data-testid="toggle-manual-quote" onClick={() => setShowManual(!showManual)}
                      className="text-xs font-bold text-primary hover:underline">
                {showManual ? "Hide headline entry" : "Or enter headline numbers by hand"}
              </button>
            </div>
          </div>

          {showManual && (
          <form onSubmit={addManualQuote} data-testid="manual-quote-form" className="space-y-3 max-w-xl">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Headline numbers</div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="manual-vendor-input" required value={manual.vendor_name}
                     onChange={(e) => setManual({ ...manual, vendor_name: e.target.value })}
                     placeholder="Vendor name" className="h-10" />
              <Input data-testid="manual-email-input" value={manual.email}
                     onChange={(e) => setManual({ ...manual, email: e.target.value })}
                     placeholder="Email" className="h-10" />
              <Input data-testid="manual-contact-input" value={manual.contact_person}
                     onChange={(e) => setManual({ ...manual, contact_person: e.target.value })}
                     placeholder="Contact person" className="h-10" />
              <Input data-testid="manual-phone-input" value={manual.contact_phone}
                     onChange={(e) => setManual({ ...manual, contact_phone: e.target.value })}
                     placeholder="Phone" className="h-10" />
              <Input data-testid="manual-total-input" required type="number" step="any" value={manual.total}
                     onChange={(e) => setManual({ ...manual, total: e.target.value })}
                     placeholder={`Total (${rfq.currency})`} className="h-10" />
              <Input data-testid="manual-discount-input" type="number" step="any" value={manual.discount}
                     onChange={(e) => setManual({ ...manual, discount: e.target.value })}
                     placeholder="Discount" className="h-10" />
              <Input data-testid="manual-delivery-input" type="number" value={manual.delivery_days}
                     onChange={(e) => setManual({ ...manual, delivery_days: e.target.value })}
                     placeholder="Delivery (days)" className="h-10" />
              <Input data-testid="manual-validity-input" type="number" value={manual.validity_days}
                     onChange={(e) => setManual({ ...manual, validity_days: e.target.value })}
                     placeholder="Validity (days)" className="h-10" />
            </div>
            <Input data-testid="manual-notes-input" value={manual.notes}
                   onChange={(e) => setManual({ ...manual, notes: e.target.value })}
                   placeholder="Notes (optional)" className="h-10" />
            <Button type="submit" data-testid="add-manual-quote-button" disabled={savingManual} variant="outline" className="h-10 gap-2 font-semibold w-full">
              {savingManual ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to comparison
            </Button>
          </form>
          )}
        </div>
      </Section>

      {openQuote && (
        <VendorQuoteDialog rfqId={id} quoteId={openQuote}
                           onClose={() => setOpenQuote("")} onChanged={load} />
      )}
    </div>
  );
}
